import {
    Backdrop,
    CircularProgress, createTheme, Grid, MuiThemeProvider, Typography
} from "@material-ui/core";
import {
    Add as AddIcon, KeyboardArrowLeft as ArrowLeftIcon,
    KeyboardArrowRight as ArrowRightIcon
} from "@material-ui/icons";
import clsx from "clsx";
import MUIDataTable from "mui-datatables";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import SelectPartNumber from "../../components/Form/SelectPartNumber";
import TextField from "../../components/Form/TextField";
import ButtonUI from "../../components/ui/ButtonUI";
import getDefaultGridOptions, {
    defaultHeaderOptions
} from "../../utils/componentUtils";
import utilsDate from "../../utils/Date";
import { useStyles } from "../../utils/DefaultStyles";

interface IFormInput {
    partNumber: any;
    partNumberId: number;
    partNumberCode: string;
    partNumberDescription: string;
    shippingQuantity: number;
    scheduledQuantity: number;
    balance: number;
    odmNumber: string;
    odmDate: any;
    referenceOdmNumber: string;
    referenceOdmDate: any;
    ccqNumber: string;
    deviation: string;
}
type props = {
    control: any;
    name: string;
    allowAdd: boolean;
    status?: string;
};

const PartNumberList: React.FC<props> = ({
    control,
    name,
    allowAdd,
    status,
}) => {
    const classes = useStyles();
    const { t: ts } = useTranslation("ShippingRequest");
    const { t: tGeneral } = useTranslation("General");
    const { t: tval } = useTranslation("Validation");
    const { enqueueSnackbar } = useSnackbar();
    const [circularLoading, setCircularLoading] = useState(false);
    const refFieldPartNumber = useRef<HTMLInputElement>(null);
    const {
        control: formGridControl,
        setError: setGridError,
        clearErrors: clearGridErrors,
        setValue: setGridValue,
    } = useForm({});
    const {
        control: formControl,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        getValues,
    } = useForm<IFormInput>({
        defaultValues: {
            partNumber: {},
            shippingQuantity: 0,
        },
    });

    const { fields, append, update } = useFieldArray({
        control,
        name: name,
    });


    const formValidation = async (data: any) => {
        var result = false;
        clearErrors();
        try {
            const schema = Yup.object().shape({
                partNumber: Yup.object()
                    .nullable()
                    .required(tval("requiredField"))
                    .shape({
                        code: Yup.string().required(tval("requiredField")),
                    }),
                shippingQuantity: Yup.number()
                    .required(tval("requiredField"))
                    .typeError(tval("mustBeNumber"))
                    .positive(tval("mustBeGreaterThan0"))
                    .integer(tval("requiredFormatInteger")),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true;
        } catch (err) {
            var errors: string[] = [];

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    if (
                        errors.filter((element) =>
                            element.includes(error.message)
                        ).length === 0
                    )
                        errors.push(error.message);

                    setError(
                        error.path?.split(".")[0] as
                            | "partNumber"
                            | "shippingQuantity",
                        { message: error.message }
                    );
                });

                errors.forEach((element) => {
                    if (element === tval("requiredField"))
                        enqueueSnackbar(tGeneral("requiredFieldsValidation"), {
                            variant: "warning",
                        });
                    else enqueueSnackbar(element, { variant: "warning" });
                });
            }

            result = false;
        }

        return result;
    };

    //Validacao sem uso de Form: personalizado para exibir no campo requestNumber_x
    const yupShippingQuantityValidation = async (
        data: any,
        rowIndex: number
    ) => {
        clearGridErrors("shippingQuantity_" + rowIndex);

        try {
            const schema = Yup.number()
                .required(tval("requiredField"))
                .typeError(tval("mustBeNumber"))
                .min(0, tval("mustBeEqualOrGreaterThan0"))
                .integer(tval("requiredFormatInteger"));

            await schema.validate(data, {
                abortEarly: true,
            });
            return true;
        } catch (err) {
            if (err instanceof Yup.ValidationError)
                setGridError("shippingQuantity_" + rowIndex, {
                    message: err.message,
                });
            return false;
        }
    };

    const onSubmit = async () => {
        let alreadyOnList = false;
        let data = getValues();

        if (!(await formValidation(data))) return;

        fields.forEach((x: any) => {
            if (x.partNumberId === Number(data.partNumber.code))
                alreadyOnList = true;
        });
        if (alreadyOnList) {
            enqueueSnackbar(ts("itemAlreadyOnList"), { variant: "warning" });
            refFieldPartNumber.current?.focus();
            return;
        }

        setCircularLoading(true);

        data.partNumberId = Number(data.partNumber.code);
        data.partNumberCode = data.partNumber.partnumberCode;
        data.partNumberDescription = data.partNumber.name;
        data.scheduledQuantity = 0;
        data.balance = 0;
        data.odmNumber = "";
        data.odmDate = "";
        data.referenceOdmNumber = "";
        data.referenceOdmDate = "";
        data.ccqNumber = "";
        data.deviation = "";

        append(data);
        reset();
        refFieldPartNumber.current?.focus();

        setCircularLoading(false);
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: "",
                },
            },
        },
    });

    function handleShippingQuantityColor(
        value: any,
        rowIndex: number,
        columnIndex: number
    ) {
        let item: any;
        item = fields[rowIndex];
        let shippingQuantity = item.shippingQuantity;
        let balance = item.balance;

        return shippingQuantity > balance ? "red" : "black";
    }

    const columns = [
        {
            name: "partNumberCode",
            label: ts("partNumberCode"),
            options: defaultHeaderOptions,
        },
        {
            name: "partNumberDescription",
            label: ts("partNumberDescription"),
            options: defaultHeaderOptions,
        },
        {
            name: "scheduledQuantity",
            label: ts("scheduledQuantity"),
            options: defaultHeaderOptions,
        },
        {
            name: "balance",
            label: ts("balance"),
            options: defaultHeaderOptions,
        },
        {
            name: "balance", //Botao para preencher
            label: " ",
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (value: any, tableMeta: any) => {
                    return renderFillButton("right", value, tableMeta);
                },
            },
        },
        {
            name: "balance",
            label: " ",
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (value: any, tableMeta: any) => {
                    return renderFillButton("center", 0, tableMeta);
                },
            },
        },
        {
            name: "shippingQuantity",
            label: ts("shippingQuantity"),
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (value: any, tableMeta: any) => {
                    return renderShippingQuantityField(value, tableMeta);
                },
                setCellProps: (
                    cellValue: string,
                    rowIndex: number,
                    columnIndex: number
                ) => ({
                    style: {
                        color: handleShippingQuantityColor(
                            cellValue,
                            rowIndex,
                            columnIndex
                        ),
                        textAlign: 'center'
                    },
                }),
            },
        },
        {
            name: "odmNumber",
            label: ts("odmNumber"),
            options: defaultHeaderOptions,
        },
        {
            name: "odmDate",
            label: ts("odmDate"),
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateTimeShort(value);
                },
            },
        },
        {
            name: "referenceOdmNumber",
            label: ts("referenceOdmNumber"),
            options: defaultHeaderOptions,
        },
        {
            name: "referenceOdmDate",
            label: ts("referenceOdmDate"),
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateTimeShort(value);
                },
            },
        },
        {
            name: "ccqNumber",
            label: ts("ccqNumber"),
            options: defaultHeaderOptions,
        },
        {
            name: "deviation",
            label: ts("deviation"),
            options: defaultHeaderOptions,
        },
    ];

    const renderFillButton = (
        direction: string,
        value: any,
        tableMeta: any
    ) => {
        return (
            <>
                {allowAdd && (
                    <ButtonUI
                        color="primary"
                        variant="outlined"
                        className={classes.smallAddButton}
                        onClick={() => {
                            if (value >= 0)
                                shippingQuantityChange(tableMeta.rowIndex, value);
                        }}
                    >
                        {direction === "right" ? (
                            <ArrowRightIcon />
                        ) : (
                            <ArrowLeftIcon />
                        )}
                    </ButtonUI>
                )}
            </>
        );
    };

    const renderShippingQuantityField = (value: any, tableMeta: any) => {
        return (
            <>
                {!allowAdd ? (
                    value
                ) : (
                    <TextField
                        name={`shippingQuantity_${tableMeta.rowIndex}`}
                        key={`shippingQuantity_${tableMeta.rowIndex}`}
                        control={formGridControl}
                        onChange={(e) =>
                            shippingQuantityChange(
                                tableMeta.rowIndex,
                                e.target.value
                            )
                        }
                        inputProps={{ maxLength: 12 }}
                        defaultValue={value}
                        className={clsx(classes.redText)}
                        fullWidth
                    />
                )}
            </>
        );
    };

    //Atualiza o valor da nota fiscal (id = indice do array de fields, enviado para a funcao update do useFieldArray)
    const shippingQuantityChange = async (id: any, value: any) => {
        let item: any = fields[id];

        item.hasError = !(await yupShippingQuantityValidation(value, id));

        item.shippingQuantity = value; //setar no fieldarray
        setGridValue("shippingQuantity_" + id, value); //setar no campo da grid

        update(id, item);
    };

    return (
        <Grid
            style={{
                marginTop: 20,
                width: "98%",
                marginLeft: -10,
                marginBottom: 10,
            }}
        >
            <Typography className={classes.root} style={{ marginLeft: 12 }}>
                {ts(allowAdd ? "titlePartNumbersAdd" : "titlePartNumbersView")}
            </Typography>

            <div className={classes.rootContent}>
                {allowAdd && (
                    <div className={classes.paper}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={1}>
                                <Grid item xs={3}>
                                    <SelectPartNumber
                                        label={ts("partNumberCode")}
                                        name={"partNumber"}
                                        control={formControl}
                                        style={{ width: "100%" }}
                                        multiple={false}
                                        inputRef={refFieldPartNumber}
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <TextField
                                        label={ts("shippingQuantity")}
                                        name={"shippingQuantity"}
                                        control={formControl}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={1}>
                                    <ButtonUI
                                        color="primary"
                                        type="submit"
                                        variant="contained"
                                        className={classes.smallAddButton}
                                        style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 20, marginLeft: '10px' }}
                                    >
                                        <AddIcon />
                                    </ButtonUI>
                                </Grid>
                            </Grid>
                        </form>
                    </div>
                )}

                <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <MuiThemeProvider theme={myTheme}>
                    <MUIDataTable
                        title=""
                        data={fields}
                        columns={columns}
                        options={{
                            ...getDefaultGridOptions(tGeneral),
                            serverSide: false,
                            pagination: true,
                            sort: false,
                            selectToolbarPlacement: "none",
                            search: true,
                            download: false,
                            print: false,
                            viewColumns: false,
                            selectableRows: "none",
                            setRowProps: (row, dataIndex, rowIndex) => {
                                let item: any;
                                item = fields[rowIndex];
                                var shippingQuantity = item.shippingQuantity;
                                var balance = row[3];

                                return {
                                    className: clsx({
                                        [classes.customYellowBackground]:
                                            row[2] === 0,
                                        [classes.customPinkBackground]:
                                            (status === "RAS" ||
                                                status === "CAN") &&
                                            (balance < 0 ||
                                                shippingQuantity > balance),
                                    }),
                                };
                            },
                        }}
                    />
                </MuiThemeProvider>
            </div>
        </Grid>
    );
};
export default PartNumberList;
